import {ITableEntityMeta} from 'entities/metadata';
import {IComparison, ITableEntityData} from 'modules/data/data-types';
import {TStateSetter} from 'shared/types/generics';

/**
 * Форматирует число, добавляя пробелы между тысячами (по правилам RU локали).
 * Дробная часть сохраняется без изменений.
 *
 * @param {number | null} x - Число для форматирования (может быть null)
 * @returns {string} Отформатированная строка с пробелами или пустая строка, если x = null
 * @example
 * numberWithSpaces(1234567.89); // "1 234 567.89"
 * numberWithSpaces(null); // ""
 */
export function numberWithSpaces(x: number | null): string {
    if (x == null) return '';

    // Разделяем целую и дробную части
    const parts = x.toString().split('.');

    // Форматируем целую часть с пробелами через Intl.NumberFormat
    parts[0] = new Intl.NumberFormat('ru-RU', {
        useGrouping: true,
        maximumFractionDigits: 20,
    }).format(Number(parts[0]));

    return parts.join('.');
}

/**
 * Проверяет, является ли строка числом (целым, дробным или с точкой в начале).
 *
 * @param {string} x - Строка для проверки
 * @returns {boolean} true, если строка соответствует числовому формату
 * @example
 * testIfNumber("123"); // true
 * testIfNumber("-12.34"); // true
 * testIfNumber(".5"); // true
 * testIfNumber("abc"); // false
 */
export function testIfNumber(x: string): boolean {
    if (x === '') return false;

    // Проверяем целые числа
    if (/^-?\d+$/.test(x)) return true;

    // Проверяем дробные числа (включая варианты: "123.", ".456")
    if (/^-?\d*\.\d+$/.test(x) || /^-?\d+\.\d*$/.test(x)) return true;

    return false;
}

/**
 * Проверяет, содержит ли массив хотя бы один элемент, который можно распознать как число.
 * Поддерживает строки с пробелами, запятыми как разделителями дробной части.
 *
 * @param {unknown[]} array - Массив для проверки
 * @returns {boolean} true, если найден хотя бы один числовой элемент
 * @example
 * arrayContainsNumbers(["123", "abc"]); // true
 * arrayContainsNumbers(["1,23", "text"]); // true (запятая заменяется на точку)
 * arrayContainsNumbers(["no", null]); // false
 */
export function arrayContainsNumbers(array: unknown[]): boolean {
    return array.some(element => {
        if (element == null) return false;

        const str = typeof element === 'string'
            ? element.replace(/\s/g, '').replace(/,/g, '.')
            : String(element);

        // Проверка целых чисел
        if (/^-?\d+$/.test(str)) return true;

        // Проверка дробных чисел
        if (/^-?\d+\.\d+$/.test(str)) return true;

        // Проверка чисел без целой части (.123)
        if (/^-?\.\d+$/.test(str)) return true;

        return false;
    });
}

/**
 * Нормализует данные сравнения (IComparison[]) для отображения в таблице:
 * - Формирует структуру полей (fields) на основе comparisonAttributes и differenceAttributes
 * - Обрабатывает числовые значения (форматирование, разницы)
 * - Обновляет метаданные (metaData) и состояние таблицы через переданные set-функции
 *
 * @param {IComparison[]} data - Массив данных сравнения
 * @param {ITableEntityMeta} metaData - Метаданные таблицы
 * @param {TStateSetter<ITableEntityMeta | undefined>} setNewMetaData -
 * Сеттер для обновления метаданных
 * @param {TStateSetter<ITableEntityData | undefined>} setNewTableData -
 * Сеттер для данных таблицы
 * @param {TStateSetter<string[]>} setAddition - Сеттер для доп. полей (addition)
 * @param {TStateSetter<string[]>} setSheet - Сеттер для списка листов (sheet)
 * @returns {void} Обновляет состояние через переданные сеттеры
 */
export function normalizeComparisonData(
    data: IComparison[],
    metaData: ITableEntityMeta,
    setNewMetaData: TStateSetter<ITableEntityMeta | undefined>,
    setNewTableData: TStateSetter<ITableEntityData | undefined>,
    setAddition: TStateSetter<string[]>,
    setSheet: TStateSetter<string[]>,
) {
    const sumColIds = [];
    const colSize = 0;
    let firstColId = 0;
    let secondColId = 0;
    const diffAttrCount = data[0]?.differenceAttributes.length;
    const fields: any[] = [];
    const rows: any[] = [];
    const newSheet: string[] = [];
    const newAddition: string[] = [];
    if (data.length) {
        data[0].comparisonAttributes.forEach((value: string, index: number) => fields.push({
            label: 'Показатель',
            key: index,
            isHidden: false,
            isSortable: true,
            type: 'STRING',
        }));
        data[0]?.differenceAttributes.slice(0, diffAttrCount / 2).forEach((value: string, index: number) => {
            fields.push({
                label: `Сумма документа 1.${index}`,
                key: fields.length,
                isSortable: true,
                type: 'STRING',
            });
            firstColId = fields.length - 1;
            sumColIds.push(firstColId);
            fields.push({
                label: `Сумма документа 2.${index}`,
                key: fields.length,
                isSortable: true,
                type: 'STRING',
            });
            secondColId = fields.length - 1;
            sumColIds.push(secondColId);
            fields.push({
                label: `Разница ${index}`,
                key: fields.length,
                isSortable: true,
                type: 'STRING',
            });
        });
        data.forEach((value, index) => {
            let row: any[] = [];
            if (value.sheet && value.sheet !== '' && !newSheet.includes(value.sheet)) {
                newSheet.push(value.sheet);
            }
            if (value.addition && value.addition !== '' && !newAddition.includes(value.addition)) {
                newAddition.push(value.addition);
            }
            row = row.concat(data[index].comparisonAttributes);
            for (let i = 0; i < diffAttrCount; i += 1) {
                row.push(data[index].differenceAttributes[i] != null
                    ? data[index].differenceAttributes[i].replace(/\s/g, '').replace(/,/g, '.')
                    : '');
            }
            if (!testIfNumber(row[firstColId]) && testIfNumber(row[secondColId])) {
                row.push(numberWithSpaces(row[secondColId]));
            }
            if (testIfNumber(row[firstColId]) && !testIfNumber(row[secondColId])) {
                row.push(numberWithSpaces(row[firstColId]));
            }
            if (!testIfNumber(row[firstColId]) && !testIfNumber(row[secondColId])) {
                row.push('');
            }
            row.push(testIfNumber(row[firstColId]) && testIfNumber(row[secondColId])
                ? numberWithSpaces(Math.abs(row[firstColId] - row[secondColId]))
                : '');
            row.push(data[index].sheet);
            row.push(data[index].addition);
            if (arrayContainsNumbers(data[index].differenceAttributes)) rows.push(row);
        });
    }
    setNewMetaData(
        {
            ...metaData,
            fields,
        },
    );
    setAddition((newAddition));
    setSheet((newSheet));
    setNewTableData({
        page: colSize,
        limit: 1,
        offset: 10,
        rows,
    });
}
